import { Spinner } from '@creditornot/cb-progress';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory, useLocation, useRouteMatch } from 'react-router';

import { getInboundMerchant, getUserContractDetails } from 'apis/merchant-onboarding-service/api';
import { OnboardingStatus } from 'apis/merchant-onboarding-service/types';
import {
  getCountryConfigs,
  getSsmoCountryConfigs,
} from 'modules/self-serve-sign-up/SelfServeFormView/SelfServeSignUpForm/api';

import useCountryConfig from '../../../common/hooks/useCountryConfig';
import { StyledAlert } from '../../shared-menu/pages/SharedMenuEditorPage/components/OfferDetailsForm/OfferDetailsFormFields.styles';
import StepPageWrapper from '../components/StepPageWrapper';
import { BASE_STEPS_PREVIEW_INTL_ID } from '../components/StepsPreview/constants';
import Wizard from '../components/Wizard/Wizard';
import BusinessInformationForm from '../components/contractInfoStepsContent/BusinessInformationForm';
import CommissionsStep from '../components/contractInfoStepsContent/CommissionsStep';
import DeviceSelectionStep from '../components/contractInfoStepsContent/DeviceSelectionStep';
import MenuPhotoshopOptionStep from '../components/contractInfoStepsContent/MenuPhotoshootOptionStep';
import ReviewInfoForm from '../components/contractInfoStepsContent/ReviewInfoForm';
import { SsmoTask, SsmoStepsIdentifiers } from '../constants';
import { ContractInfoWizardStep, ContractInfoWizardStepType } from '../types';

type PageProps = {
  merchantId: string;
  onboardingStatus: OnboardingStatus;
};

const isSelfServeStep = (stepParam: string): boolean =>
  Object.values(SsmoStepsIdentifiers).includes(stepParam as ContractInfoWizardStepType);

const validateStepParam = (param: string | null) => {
  if (!param) {
    return SsmoStepsIdentifiers.BusinessDetails;
  }

  const isValidParam = isSelfServeStep(param);

  return isValidParam ? param.toLowerCase() : SsmoStepsIdentifiers.BusinessDetails;
};

const ContractInfoWizardPage = ({ merchantId }: PageProps) => {
  const intl = useIntl();
  const { search } = useLocation();
  const { url } = useRouteMatch();
  const history = useHistory();
  const { formatMessage } = useIntl();

  const [stepParam, setStepParam] = useState<string>(SsmoStepsIdentifiers.BusinessDetails);
  const queryClient = useQueryClient();

  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const invalidateCache = () => queryClient.invalidateQueries(['/merchant-admin/inbound-merchants', merchantId]);

  const { data: inboundMerchant, isFetching: isFetchingInboundMerchant } = useQuery(
    ['/merchant-admin/inbound-merchants', merchantId],
    () => getInboundMerchant(merchantId),
    {
      refetchOnWindowFocus: false,
    },
  );

  const {
    data: countriesConfig,
    isLoading: isCountriesConfigsLoading,
    error: countriesConfigLoadError,
  } = useQuery(['merchant-admin/config'], getCountryConfigs, {
    refetchOnWindowFocus: false,
  });

  const {
    isLoading: isSsmoCountryConfigsLoading,
    data: ssmoCountryConfig,
    error: ssmoCountryConfigLoadError,
  } = useQuery(['/merchant-admin/config/ssmo'], getSsmoCountryConfigs, {
    refetchOnWindowFocus: false,
  });

  const {
    countryConfig,
    isFetching: isFetchingCountryConfig,
    error: countryConfigLoadError,
  } = useCountryConfig(inboundMerchant?.country || null, {
    refetchOnWindowFocus: false,
  });

  const { data: contracts, isFetching: isContractDetailsLoading } = useQuery(
    ['merchant-admin/me/contracts'],
    () => getUserContractDetails(),
    {
      refetchOnWindowFocus: false,
    },
  );

  const wizardData: ContractInfoWizardStep[] = useMemo(() => {
    if (!inboundMerchant || !ssmoCountryConfig || !countriesConfig || !countryConfig) {
      return [];
    }

    const TOTAL_NUMBER_OF_GROUPS = 5;

    const calculatePercentage = ({
      groupNumber,
      progressForGroup,
      totalNumberOfGroups,
    }: {
      groupNumber: number;
      progressForGroup: number;
      totalNumberOfGroups: number;
    }) => {
      // percentageFilled calculation for definition goes as follows
      // (current group number - 1 + progress for current group) / total number of groups
      // example:
      //  - The first step in the first group is supposed to fill half a bar. That's 1/2 of a bar. It's the group number 1 and the total number of groups is 4:
      //    percentageFilled: (1 - 1 + 1/2) / 4,
      //  - The second step in the third group is supposed to fill 2/3 of a bar. It's the group number 3 and the total number of groups is 4:
      //    percentageFilled: (3 - 1 + 2/3) / 4

      return (groupNumber - 1 + progressForGroup) / totalNumberOfGroups;
    };

    return [
      {
        title: formatMessage({ id: 'self-serve-sign-up.wizard.step-titles.business-info' }),
        subtitle: formatMessage({ id: 'self-serve-sign-up.wizard.step-subtitles.business-info' }),
        identifier: SsmoStepsIdentifiers.BusinessDetails,
        component: props => (
          <BusinessInformationForm {...props} inboundMerchant={inboundMerchant} countriesConfigs={countriesConfig} />
        ),
        percentageFilled: calculatePercentage({
          groupNumber: 1,
          progressForGroup: 1 / 1,
          totalNumberOfGroups: TOTAL_NUMBER_OF_GROUPS,
        }),
      },
      {
        title: formatMessage({ id: 'self-serve-sign-up.wizard.step-titles.menu-photo-shoot' }),
        subtitle: formatMessage({ id: 'self-serve-sign-up.wizard.step-subtitles.menu-photo-shoot' }),
        identifier: SsmoStepsIdentifiers.MenuPhotoShoot,
        component: props => (
          <MenuPhotoshopOptionStep
            {...props}
            inboundMerchant={inboundMerchant}
            ssmoCountryConfig={ssmoCountryConfig}
            countryConfig={countryConfig}
          />
        ),
        percentageFilled: calculatePercentage({
          groupNumber: 2,
          progressForGroup: 1 / 1,
          totalNumberOfGroups: TOTAL_NUMBER_OF_GROUPS,
        }),
      },
      {
        title: formatMessage({ id: 'self-serve-sign-up.wizard.step-titles.getting-set-up' }),
        subtitle: formatMessage({ id: 'self-serve-sign-up.wizard.step-subtitles.device-selection' }),
        identifier: SsmoStepsIdentifiers.DeviceSelection,
        component: props => (
          <DeviceSelectionStep
            {...props}
            inboundMerchant={inboundMerchant}
            ssmoCountryConfig={ssmoCountryConfig}
            countryConfig={countryConfig}
          />
        ),
        percentageFilled: calculatePercentage({
          groupNumber: 3,
          progressForGroup: 1 / 1,
          totalNumberOfGroups: TOTAL_NUMBER_OF_GROUPS,
        }),
      },
      {
        title: formatMessage({ id: 'self-serve-sign-up.wizard.step-titles.service-pricing' }),
        subtitle: formatMessage({ id: 'self-serve-sign-up.wizard.step-subtitles.service-pricing' }),
        identifier: SsmoStepsIdentifiers.ServicePricing,
        component: props => (
          <CommissionsStep {...props} inboundMerchant={inboundMerchant} ssmoCountryConfig={ssmoCountryConfig} />
        ),
        percentageFilled: calculatePercentage({
          groupNumber: 4,
          progressForGroup: 1 / 1,
          totalNumberOfGroups: TOTAL_NUMBER_OF_GROUPS,
        }),
      },
      {
        title: formatMessage({ id: 'self-serve-sign-up.wizard.step-titles.last-review' }),
        identifier: SsmoStepsIdentifiers.Review,
        component: props => (
          <ReviewInfoForm {...props} inboundMerchant={inboundMerchant} countryConfig={countryConfig} />
        ),
        percentageFilled: calculatePercentage({
          groupNumber: 5,
          progressForGroup: 1 / 1,
          totalNumberOfGroups: TOTAL_NUMBER_OF_GROUPS,
        }),
      },
    ];
  }, [inboundMerchant, ssmoCountryConfig, countriesConfig, formatMessage, countryConfig]);

  useEffect(() => {
    const stepParam = searchParams.get('step');
    const hasWizardData = wizardData.length > 0;

    if (!stepParam && hasWizardData) {
      history.push(`${url}?step=${wizardData[0].identifier}`);
      return;
    }
    if (hasWizardData) {
      const newStepParam = validateStepParam(searchParams.get('step')) || wizardData[0].identifier;
      setStepParam(newStepParam);
    }
  }, [history, search, searchParams, wizardData, url]);

  if (countriesConfigLoadError || ssmoCountryConfigLoadError || countryConfigLoadError) {
    return <StyledAlert variant="error" title={intl.formatMessage({ id: 'self-serve-sign-up.error-unknown' })} />;
  }

  if (
    !inboundMerchant ||
    isFetchingInboundMerchant ||
    isCountriesConfigsLoading ||
    !countriesConfig ||
    isSsmoCountryConfigsLoading ||
    !ssmoCountryConfig ||
    wizardData.length === 0 ||
    isFetchingCountryConfig ||
    isContractDetailsLoading
  ) {
    return (
      <StepPageWrapper
        title={formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.build-partnership.tasks.create-agreement.title` })}
        step={SsmoStepsIdentifiers.ContractDetails}
        task={SsmoTask.CONTRACT_SIGNING}
      >
        <Spinner size="large" />
      </StepPageWrapper>
    );
  }

  if (contracts?.[0]?.signingUrl) {
    history.push('/onboarding-page/sign-contract');
  }

  return (
    <StepPageWrapper
      title={formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.build-partnership.tasks.create-agreement.title` })}
      step={SsmoStepsIdentifiers.ContractDetails}
      task={SsmoTask.CONTRACT_SIGNING}
    >
      <Wizard
        stepsCount={5}
        invalidateCache={invalidateCache}
        wizardData={wizardData}
        currentStepIdentifier={stepParam}
      />
    </StepPageWrapper>
  );
};

export default ContractInfoWizardPage;
